import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, TOOL_REGISTRY } from '../utils/constants';
import { DataExportPanel } from '../components/admin/DataExportPanel';

const ALL_MODULES = [
  { id: 'sales', label: 'Sales' },
  { id: 'production', label: 'Production' },
  { id: 'management', label: 'Management' },
  { id: 'human-resources', label: 'Human Resources' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'customer-care', label: 'Customer Care' },
  { id: 'training', label: 'Training' },
];

const REIFY_LOGO = 'https://customer-assets.emergentagent.com/job_dbd68d1b-28e6-42d0-902a-82a09fb5a2e9/artifacts/65npxqdg_atdzxq91_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2008_54_02%20AM.png';

export default function PlatformAdminPage({ currentUser, onBackToGears, onLogout }) {
  const [tab, setTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [recoveryError, setRecoveryError] = useState('');
  const [keepUserId, setKeepUserId] = useState('');
  const [resolvingRecovery, setResolvingRecovery] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/companies`);
      setCompanies(res.data);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchCompanies(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchCompanies, fetchStats]);

  const handleRecoveryLookup = async () => {
    const email = recoveryEmail.trim().toLowerCase();
    if (!email) return;
    setRecoveryError('');
    setRecoveryResult(null);
    setKeepUserId('');
    setRecoveryLoading(true);
    try {
      const res = await axios.get(`${API}/platform/users/by-email/${encodeURIComponent(email)}`);
      setRecoveryResult(res.data);
      if ((res.data?.users || []).length === 1) {
        setKeepUserId(res.data.users[0].id);
      }
    } catch (err) {
      setRecoveryError(err.response?.data?.detail || 'Failed to lookup accounts for this email');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleResolveRecovery = async () => {
    const email = recoveryEmail.trim().toLowerCase();
    if (!email || !keepUserId) {
      setRecoveryError('Select the account to keep before resolving.');
      return;
    }

    setResolvingRecovery(true);
    setRecoveryError('');
    try {
      const res = await axios.post(`${API}/platform/users/resolve-duplicate-email`, {
        email,
        keep_user_id: keepUserId,
        deactivate_other_accounts: true,
        unlock_kept_account: true,
      });
      setRecoveryResult(prev => ({
        ...(prev || {}),
        resolve_message: res.data?.message || 'Resolved',
        kept_user: res.data?.kept_user || null,
        deactivated_user_ids: res.data?.deactivated_user_ids || [],
      }));
      await handleRecoveryLookup();
      alert('User recovery complete. Kept account unlocked and duplicates deactivated.');
    } catch (err) {
      setRecoveryError(err.response?.data?.detail || 'Failed to resolve duplicate accounts');
    } finally {
      setResolvingRecovery(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" data-testid="platform-admin-page">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBackToGears ? (
              <button
                onClick={onBackToGears}
                className="text-gray-400 hover:text-white transition-colors"
                data-testid="platform-admin-back-btn"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            ) : null}
            <div>
              <div className="flex items-center gap-2">
                <img src={REIFY_LOGO} alt="Reify" className="h-7 object-contain" />
              </div>
              <p className="text-xs text-gray-500">Platform Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <a
              href={`${window.location.origin}`}
              onClick={(e) => {
                e.preventDefault();
                const msg = 'To access the Dev Sandbox, log in as:\n\nEmail: dev@reifysystems.com\nPassword: ReifyDev1$ (change on first login)\n\nThis is a sandboxed company with all modules & tools enabled for testing.';
                if (window.confirm(msg + '\n\nLog out now?')) {
                  onLogout?.();
                }
              }}
              className="px-2 py-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded font-medium cursor-pointer transition-colors"
              data-testid="dev-sandbox-btn"
            >
              DEV SANDBOX
            </a>
            <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded font-medium">PLATFORM ADMIN</span>
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/platform/backfill-tools`);
                  alert(`Backfill complete: ${res.data.message}\n${res.data.updated?.length ? 'Updated: ' + res.data.updated.join(', ') : 'All companies already up to date'}`);
                } catch (err) {
                  alert('Failed: ' + (err.response?.data?.detail || err.message));
                }
              }}
              className="px-2 py-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded font-medium cursor-pointer transition-colors"
              data-testid="backfill-tools-btn"
            >
              BACKFILL TOOLS
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('This will find and tag any documents missing a company ID.\nSafe to run multiple times. Continue?')) return;
                try {
                  const res = await axios.post(`${API}/platform/retag-data`);
                  const d = res.data;
                  const collections = Object.entries(d.collections_updated || {}).map(([k,v]) => `  ${k}: ${v} docs`).join('\n');
                  alert(`Re-tag Complete!\n\n${d.message}\n\n${collections || 'No untagged documents found.'}`);
                } catch (err) {
                  alert('Re-tag failed: ' + (err.response?.data?.detail || err.message));
                }
              }}
              className="px-2 py-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded font-medium cursor-pointer transition-colors"
              data-testid="retag-data-btn"
            >
              RE-TAG DATA
            </button>
            {onLogout && (
              <button
                onClick={onLogout}
                className="px-3 py-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors text-xs font-medium"
                data-testid="platform-admin-logout-btn"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <StatCard label="Total Companies" value={stats.total_companies} color="cyan" />
            <StatCard label="Active Companies" value={stats.active_companies} color="green" />
            <StatCard label="Total Users" value={stats.total_users} color="violet" />
          </div>
        )}

        {/* Emergency User Recovery */}
        <div className="mb-8 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4" data-testid="emergency-user-recovery-panel">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-amber-300 uppercase tracking-wider mb-1">Emergency User Recovery</label>
              <input
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                placeholder="user@company.com"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
                data-testid="recovery-email-input"
              />
            </div>
            <button
              onClick={handleRecoveryLookup}
              disabled={recoveryLoading || !recoveryEmail.trim()}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="recovery-lookup-btn"
            >
              {recoveryLoading ? 'Checking...' : 'Find Accounts'}
            </button>
            <button
              onClick={handleResolveRecovery}
              disabled={resolvingRecovery || !keepUserId || !recoveryResult}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="recovery-resolve-btn"
            >
              {resolvingRecovery
                ? 'Resolving...'
                : ((recoveryResult?.count || 0) > 1 ? 'Keep Selected + Disable Others' : 'Unlock Selected Account')}
            </button>
          </div>

          {recoveryError ? (
            <p className="text-red-400 text-xs mt-3" data-testid="recovery-error">{recoveryError}</p>
          ) : null}

          {recoveryResult ? (
            <div className="mt-3 space-y-2" data-testid="recovery-results">
              <p className="text-xs text-gray-300">
                Found <span className="font-semibold">{recoveryResult.count || 0}</span> account(s) for this email.
              </p>
              {(recoveryResult.users || []).map((u) => (
                <label
                  key={u.id}
                  className="flex items-center justify-between gap-3 bg-gray-900/70 border border-gray-800 rounded-lg px-3 py-2"
                  data-testid={`recovery-user-${u.id}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="recovery-keep-user"
                      checked={keepUserId === u.id}
                      onChange={() => setKeepUserId(u.id)}
                      data-testid={`recovery-keep-user-${u.id}`}
                    />
                    <div>
                      <p className="text-sm text-gray-100">{u.full_name || 'Unnamed User'} ({u.email})</p>
                      <p className="text-xs text-gray-400">Company: {u.company_name || u.company_id || 'Platform'}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <p className={u.is_active !== false ? 'text-emerald-400' : 'text-red-400'}>{u.is_active !== false ? 'Active' : 'Inactive'}</p>
                    <p className={u.is_locked ? 'text-red-400' : 'text-gray-500'}>{u.is_locked ? 'Locked' : 'Unlocked'}</p>
                  </div>
                </label>
              ))}
            </div>
          ) : null}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-800">
          {['companies', 'exports', 'activity'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors capitalize ${
                tab === t ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'companies' && (
          <CompaniesTab
            companies={companies}
            onRefresh={() => { fetchCompanies(); fetchStats(); }}
            onSelect={setSelectedCompany}
            onCreateNew={() => setShowCreateModal(true)}
          />
        )}

        {tab === 'activity' && (
          <div className="text-center text-gray-500 py-16">
            <p className="text-lg">Activity log coming soon</p>
            <p className="text-sm mt-1">Platform-wide activity tracking</p>
          </div>
        )}

        {tab === 'exports' && (
          <div data-testid="platform-admin-exports-tab">
            <DataExportPanel isPlatformAdmin defaultCompanyId="" />
          </div>
        )}
      </div>

      {/* Create Company Modal */}
      {showCreateModal && (
        <CreateCompanyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { fetchCompanies(); fetchStats(); setShowCreateModal(false); }}
        />
      )}

      {/* Company Detail Modal */}
      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          onUpdated={() => { fetchCompanies(); fetchStats(); }}
          onCreateUser={() => setShowUserModal(true)}
        />
      )}

      {/* Create User Modal */}
      {showUserModal && selectedCompany && (
        <CreateUserModal
          companyId={selectedCompany.id}
          companyName={selectedCompany.name}
          onClose={() => setShowUserModal(false)}
          onCreated={() => { fetchCompanies(); setShowUserModal(false); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colorMap = {
    cyan: 'text-cyan-400 bg-cyan-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    violet: 'text-violet-400 bg-violet-500/10',
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-3xl font-bold ${colorMap[color]?.split(' ')[0] || 'text-white'}`}>{value}</p>
    </div>
  );
}

function CompaniesTab({ companies, onRefresh, onSelect, onCreateNew }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-200">Companies</h2>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium transition-colors"
          data-testid="create-company-btn"
        >
          + New Company
        </button>
      </div>
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full" data-testid="companies-table">
          <thead>
            <tr className="border-b border-gray-800 text-xs uppercase text-gray-500">
              <th className="text-left px-5 py-3">Company</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-center px-5 py-3">Users</th>
              <th className="text-center px-5 py-3">Modules</th>
              <th className="text-center px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map(c => (
              <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/40 transition-colors">
                <td className="px-5 py-3.5">
                  <span className="font-medium text-gray-200">{c.name}</span>
                </td>
                <td className="px-5 py-3.5 text-sm text-gray-400">{c.email || '-'}</td>
                <td className="px-5 py-3.5 text-center text-sm text-gray-400">{c.user_count || 0}</td>
                <td className="px-5 py-3.5 text-center text-sm text-gray-400">{(c.modules_enabled || []).length}</td>
                <td className="px-5 py-3.5 text-center">
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    c.is_active !== false ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {c.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <button
                    onClick={() => onSelect(c)}
                    className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                    data-testid={`manage-company-${c.id}`}
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {companies.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-500">No companies yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModuleToolSelector({ modules, tools, onModulesChange, onToolsChange }) {
  const [expanded, setExpanded] = useState({});

  const toggleModule = (modId) => {
    const newModules = modules.includes(modId)
      ? modules.filter(m => m !== modId)
      : [...modules, modId];
    onModulesChange(newModules);
    // When enabling a module, default all tools; when disabling, remove from tools
    const newTools = { ...tools };
    if (!modules.includes(modId)) {
      // Enabling — set all tools
      const modTools = TOOL_REGISTRY[modId] || [];
      if (modTools.length > 0) {
        newTools[modId] = modTools.map(t => t.id);
      }
    } else {
      // Disabling — remove tools
      delete newTools[modId];
    }
    onToolsChange(newTools);
  };

  const toggleTool = (modId, toolId) => {
    const modTools = tools[modId] || (TOOL_REGISTRY[modId] || []).map(t => t.id);
    const newModTools = modTools.includes(toolId)
      ? modTools.filter(t => t !== toolId)
      : [...modTools, toolId];
    onToolsChange({ ...tools, [modId]: newModTools });
  };

  const allToolIds = (modId) => (TOOL_REGISTRY[modId] || []).map(t => t.id);
  const getActiveTools = (modId) => tools[modId] || allToolIds(modId);

  return (
    <div className="space-y-2">
      {ALL_MODULES.map(m => {
        const isEnabled = modules.includes(m.id);
        const modTools = TOOL_REGISTRY[m.id] || [];
        const hasTools = modTools.length > 0;
        const isExpanded = expanded[m.id];
        const activeTools = getActiveTools(m.id);
        const allSelected = hasTools && activeTools.length === modTools.length;

        return (
          <div key={m.id} className={`rounded-lg border ${isEnabled ? 'border-cyan-500/30 bg-gray-800/50' : 'border-gray-800 bg-gray-900/30'}`}>
            <div className="flex items-center gap-2 px-3 py-2">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => toggleModule(m.id)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
                data-testid={`module-toggle-${m.id}`}
              />
              <span className={`text-sm font-medium flex-1 ${isEnabled ? 'text-gray-200' : 'text-gray-500'}`}>{m.label}</span>
              {isEnabled && hasTools && (
                <button
                  type="button"
                  onClick={() => setExpanded(p => ({ ...p, [m.id]: !p[m.id] }))}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  data-testid={`expand-tools-${m.id}`}
                >
                  <span>{activeTools.length}/{modTools.length} tools</span>
                  <svg className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              )}
            </div>
            {isEnabled && hasTools && isExpanded && (
              <div className="px-3 pb-3 pt-1 border-t border-gray-800/50">
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={() => onToolsChange({ ...tools, [m.id]: allSelected ? [] : allToolIds(m.id) })}
                    className="text-[10px] text-gray-500 hover:text-gray-300"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {modTools.map(t => (
                    <label key={t.id} className="flex items-center gap-1.5 cursor-pointer group" title={t.description}>
                      <input
                        type="checkbox"
                        checked={activeTools.includes(t.id)}
                        onChange={() => toggleTool(m.id, t.id)}
                        className="w-3.5 h-3.5 rounded border-gray-700 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
                        data-testid={`tool-toggle-${m.id}-${t.id}`}
                      />
                      <span className="text-xs text-gray-400 group-hover:text-gray-200 truncate">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreateCompanyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: '', company_email: '', company_phone: '', company_address: '', company_website: '',
    admin_name: '', admin_email: '', admin_password: '',
    modules_enabled: ALL_MODULES.map(m => m.id),
    tools_enabled: {},
    send_welcome_email: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleModule = (modId) => {
    setForm(p => ({
      ...p,
      modules_enabled: p.modules_enabled.includes(modId)
        ? p.modules_enabled.filter(m => m !== modId)
        : [...p.modules_enabled, modId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(`${API}/platform/provision-company`, {
        ...form,
        tools_enabled: form.tools_enabled,
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to provision company');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="provision-success-modal">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6 text-center">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Company Provisioned</h3>
          <p className="text-gray-400 text-sm mb-1"><strong>{success.company?.name}</strong> is ready to go.</p>
          <p className="text-gray-500 text-xs mb-4">
            Admin: {success.admin_email}
            {success.welcome_email_sent ? ' — Welcome email sent' : ' — Welcome email not sent (check SMTP config)'}
          </p>
          <button
            onClick={() => { onCreated(); onClose(); }}
            className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
            data-testid="provision-done-btn"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="create-company-modal">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-white mb-1">Provision New Company</h3>
        <p className="text-xs text-gray-500 mb-4">Create company, admin user, and select modules — all in one step.</p>
        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Company Info */}
          <div>
            <label className="block text-xs text-cyan-400 uppercase tracking-wider font-medium mb-2">Company Information</label>
            <div className="space-y-3">
              <Field label="Company Name" value={form.company_name} onChange={v => setForm(p => ({ ...p, company_name: v }))} required />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" type="email" value={form.company_email} onChange={v => setForm(p => ({ ...p, company_email: v }))} placeholder="office@company.com" />
                <Field label="Phone" value={form.company_phone} onChange={v => setForm(p => ({ ...p, company_phone: v }))} placeholder="555-1234" />
              </div>
              <Field label="Address" value={form.company_address} onChange={v => setForm(p => ({ ...p, company_address: v }))} placeholder="123 Main St, City" />
              <Field label="Website" value={form.company_website} onChange={v => setForm(p => ({ ...p, company_website: v }))} placeholder="https://www.company.com" />
            </div>
          </div>

          {/* Admin User */}
          <div>
            <label className="block text-xs text-cyan-400 uppercase tracking-wider font-medium mb-2">Admin User Credentials</label>
            <div className="space-y-3">
              <Field label="Admin Full Name" value={form.admin_name} onChange={v => setForm(p => ({ ...p, admin_name: v }))} required />
              <Field label="Admin Email" type="email" value={form.admin_email} onChange={v => setForm(p => ({ ...p, admin_email: v }))} required />
              <Field label="Temporary Password" type="password" value={form.admin_password} onChange={v => setForm(p => ({ ...p, admin_password: v }))} required />
              <p className="text-xs text-gray-600">They'll be prompted to change this on first login.</p>
            </div>
          </div>

          {/* Modules & Tools */}
          <div>
            <label className="block text-xs text-cyan-400 uppercase tracking-wider font-medium mb-2">Modules & Tools</label>
            <ModuleToolSelector
              modules={form.modules_enabled}
              tools={form.tools_enabled}
              onModulesChange={mods => setForm(p => ({ ...p, modules_enabled: mods }))}
              onToolsChange={tools => setForm(p => ({ ...p, tools_enabled: tools }))}
            />
          </div>

          {/* Send Welcome Email */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.send_welcome_email}
              onChange={() => setForm(p => ({ ...p, send_welcome_email: !p.send_welcome_email }))}
              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
              data-testid="send-welcome-email-toggle"
            />
            <span className="text-sm text-gray-300">Send welcome email with login credentials</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="create-company-submit">
              {saving ? 'Provisioning...' : 'Provision Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompanyDetailModal({ company, onClose, onUpdated, onCreateUser }) {
  const [modules, setModules] = useState(company.modules_enabled || []);
  const [tools, setTools] = useState(company.tools_enabled || {});
  const [isActive, setIsActive] = useState(company.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [name, setName] = useState(company.name);
  const [email, setEmail] = useState(company.email || '');
  const [website, setWebsite] = useState(company.website || '');
  const [smtpEmail, setSmtpEmail] = useState(company.smtp_email || '');
  const [smtpPassword, setSmtpPassword] = useState(company.smtp_password || '');

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('');
    try {
      await axios.put(`${API}/platform/companies/${company.id}`, {
        name, email, website, modules_enabled: modules, tools_enabled: tools,
        smtp_email: smtpEmail, smtp_password: smtpPassword,
      });
      setSaveStatus('success');
      onUpdated();
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) {
      console.error('Failed to update company:', err);
      setSaveStatus(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (isActive && !window.confirm(`Deactivate "${company.name}"? Their users won't be able to log in.`)) return;
    if (!isActive && !window.confirm(`Reactivate "${company.name}"? Existing users will regain login access.`)) return;

    try {
      if (isActive) {
        await axios.delete(`${API}/platform/companies/${company.id}`);
      } else {
        await axios.post(`${API}/platform/companies/${company.id}/reactivate`);
      }
      setIsActive(!isActive);
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Failed to change company status:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="company-detail-modal">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white">Manage Company</h3>
          <span
            className={`px-2 py-0.5 text-xs rounded-full font-medium ${isActive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}
            data-testid="company-status-badge"
          >
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Company Name" value={name} onChange={setName} />
          <Field label="Contact Email" value={email} onChange={setEmail} />
          <Field label="Website" value={website} onChange={setWebsite} placeholder="https://www.company.com" />

          {/* Modules & Tools */}
          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Modules & Tools</label>
            <ModuleToolSelector
              modules={modules}
              tools={tools}
              onModulesChange={setModules}
              onToolsChange={setTools}
            />
          </div>

          {/* Email Settings */}
          <div className="pt-2 border-t border-gray-800">
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Email Configuration (Optional)</label>
            <div className="space-y-3">
              <Field label="SMTP Email" value={smtpEmail} onChange={setSmtpEmail} placeholder="company@gmail.com" />
              <Field label="SMTP Password / App Password" type="password" value={smtpPassword} onChange={setSmtpPassword} placeholder="App password" />
            </div>
          </div>

          {/* Users */}
          <div className="pt-2 border-t border-gray-800 flex items-center justify-between">
            <span className="text-sm text-gray-400">{company.user_count || 0} user(s)</span>
            <button
              onClick={onCreateUser}
              className="text-sm text-cyan-400 hover:text-cyan-300 font-medium"
              data-testid="add-user-btn"
            >
              + Add User
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-800">
          <button
            onClick={handleToggleActive}
            className={`text-sm ${isActive ? 'text-red-400 hover:text-red-300' : 'text-emerald-400 hover:text-emerald-300'}`}
            data-testid={isActive ? 'deactivate-company-btn' : 'reactivate-company-btn'}
          >
            {isActive ? 'Deactivate Company' : 'Reactivate Company'}
          </button>
          <div className="flex items-center gap-3">
            {saveStatus === 'success' && (
              <span className="text-sm text-emerald-400 font-medium" data-testid="save-success-msg">Saved!</span>
            )}
            {saveStatus && saveStatus !== 'success' && (
              <span className="text-sm text-red-400 font-medium" data-testid="save-error-msg">{saveStatus}</span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="save-company-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ companyId, companyName, onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', full_name: '', password: '', role: 'employee' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/platform/companies/${companyId}/users`, form);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="create-user-modal">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Add User</h3>
        <p className="text-xs text-gray-500 mb-4">For {companyName}</p>
        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Full Name" value={form.full_name} onChange={v => setForm(p => ({ ...p, full_name: v }))} required />
          <Field label="Email" type="email" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} required />
          <Field label="Password" type="password" value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} required />
          <div>
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Role</label>
            <select
              value={form.role}
              onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-cyan-500 focus:outline-none"
              data-testid="user-role-select"
            >
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="create-user-submit">
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, required, name }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={value}
        name={name}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-cyan-500 focus:outline-none"
        data-testid={`field-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
      />
    </div>
  );
}
